import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, IconButton, Typography, Button, LinearProgress, Chip, Paper, CircularProgress,
  useTheme, useMediaQuery, Tooltip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SettingsIcon from '@mui/icons-material/Settings';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useDashboard } from '../../contexts/DashboardContext';
import FormFieldRenderer from '../FormFieldRenderer';
import { toDisplayString } from '../../utils/flowResultUtils';
import { useVoiceConversation } from '../../hooks/useVoiceConversation';
import DemoVoiceControls from '../demo/DemoVoiceControls';
import { normalizeInterviewPhases } from '../../utils/interviewFlowRuntime';

export default function DashboardChat({ darkMode }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSFF = useMediaQuery(theme.breakpoints.down('md'));
  const [input, setInput] = useState('');
  const [formData, setFormData] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const {
    runDefaultFlow, setLastInput, setError, config, structuredOutput, loading, activeFlowState, submitFormToFlow,
    interviewPhaseId, setInterviewPhaseId, exportInterviewSession, sharedChatHistory, sharedTranscript,
  } = useDashboard();

  const interviewPhases = useMemo(
    () => normalizeInterviewPhases(config?.metadata?.interviewFlow?.phases),
    [config?.metadata?.interviewFlow?.phases],
  );

  const phaseSelectValue = useMemo(() => {
    if (!interviewPhases.length) return '';
    if (interviewPhaseId && interviewPhases.some((p) => p.id === interviewPhaseId)) return interviewPhaseId;
    return interviewPhases[0].id;
  }, [interviewPhases, interviewPhaseId]);

  // Reset form data when form fields change (e.g. new form pause)
  useEffect(() => {
    if (activeFlowState.waitingForForm && activeFlowState.formFields?.length > 0) {
      const initial = {};
      activeFlowState.formFields.forEach((f) => {
        const k = f.name || f.id;
        if (k && f.value != null) initial[k] = f.value;
      });
      setFormData((prev) => ({ ...initial, ...prev }));
    } else {
      setFormData({});
    }
  }, [activeFlowState.waitingForForm, activeFlowState.formFields]);
  const hasDefaultFlow = Boolean(config?.defaultFlowId || config?.default_flow_id)
    && !config?.studioPreviewInterview;
  const isDemoReadOnly = Boolean(config?.studioPreviewInterview || config?.demoReadOnly);
  const appId = config?._id || config?.id;
  const interviewMeta = config?.metadata?.interviewFlow;
  const citizenMode = Boolean(config?.citizenMode || interviewMeta?.citizenMode);
  const hasInterviewExportable = Boolean(
    interviewMeta && ((sharedChatHistory?.length || 0) > 0 || (sharedTranscript?.length || 0) > 0),
  );

  const sendText = useCallback(async (raw) => {
    const q = String(raw || '').trim();
    if (!q) return;
    setLastInput(q);
    setError(null);
    await runDefaultFlow(q);
    setInput('');
  }, [runDefaultFlow, setLastInput, setError]);

  const handleSend = useCallback(async () => {
    await sendText(input);
  }, [input, sendText]);

  const voice = useVoiceConversation({
    onDictate: (text) => setInput((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}${text}`),
    onUtterance: (text) => { sendText(text); },
  });

  useEffect(() => {
    if (!voice.conversationMode || loading) return;
    const txt = toDisplayString(
      structuredOutput?.ai_response
        ?? structuredOutput?.message
        ?? structuredOutput?.routing?.rationale
        ?? structuredOutput?.routing?.primaryTrackLabel
        ?? '',
      '',
    );
    if (txt) voice.speakReply(txt);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- speak when result settles
  }, [structuredOutput, loading, voice.conversationMode]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFormFieldChange = useCallback((fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleFormSubmit = useCallback(async () => {
    if (!submitFormToFlow) return;
    setFormSubmitting(true);
    try {
      await submitFormToFlow(formData);
    } finally {
      setFormSubmitting(false);
    }
  }, [submitFormToFlow, formData]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        {isDemoReadOnly
          ? 'Sample conversation — read-only demo. Sign up to run live AI screening on your rubric.'
          : hasDefaultFlow
            ? 'Search / Chat — triggers workflow execution. Global filters are passed to flows.'
            : 'Search / Chat — configure a default flow in App settings to run workflows'}
      </Typography>
      {interviewPhases.length > 0 && !citizenMode && (
        <FormControl size="small" sx={{ mb: 2, minWidth: 220, maxWidth: '100%' }}>
          <InputLabel id="dash-interview-phase-label">Interview phase</InputLabel>
          <Select
            labelId="dash-interview-phase-label"
            label="Interview phase"
            value={phaseSelectValue}
            onChange={(e) => {
              const v = String(e.target.value || '');
              setInterviewPhaseId(v || null);
            }}
          >
            {interviewPhases.map((ph) => (
              <MenuItem key={ph.id} value={ph.id}>{ph.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {interviewMeta && (
        <Tooltip title={hasInterviewExportable ? 'Download JSON (respects export redaction settings)' : 'Run a turn first to include chat or transcript'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={!hasInterviewExportable}
              onClick={() => exportInterviewSession?.()}
              sx={{ mb: 2, alignSelf: 'flex-start' }}
            >
              Export interview session
            </Button>
          </span>
        </Tooltip>
      )}
      {!hasDefaultFlow && appId && (
        <Button
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => navigate(`/dashboard-builder?edit=${appId}`)}
          sx={{ mb: 2 }}
        >
          Configure app
        </Button>
      )}
      {(loading || activeFlowState.isActive) && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LinearProgress
              variant={activeFlowState.progress ? 'determinate' : 'indeterminate'}
              value={activeFlowState.progress || 0}
              sx={{ flex: 1, height: 6, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {activeFlowState.elapsedSeconds ?? 0}s
            </Typography>
          </Box>
          {activeFlowState.currentNode && (
            <Chip size="small" label={activeFlowState.currentNode} sx={{ fontSize: '0.7rem', mr: 0.5 }} />
          )}
          {activeFlowState.waitingForForm && !activeFlowState.formFields?.length && (
            <Chip size="small" color="warning" label="Waiting for form input — form fields will appear below when ready" sx={{ fontSize: '0.7rem' }} />
          )}
          {loading && !activeFlowState.isActive && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Starting workflow…
            </Typography>
          )}
        </Box>
      )}
      {activeFlowState.waitingForForm && activeFlowState.formFields?.length > 0 && (
        <Paper elevation={2} sx={{ p: 2, mb: 2, border: '2px solid', borderColor: 'warning.main', bgcolor: 'action.hover' }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            {activeFlowState.formTitle || 'Form Input Required'}
          </Typography>
          {activeFlowState.formDescription && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {activeFlowState.formDescription}
            </Typography>
          )}
          {activeFlowState.formAiResponse && (
            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{activeFlowState.formAiResponse}</Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {activeFlowState.formFields.map((field, idx) => (
              <FormFieldRenderer
                key={field.name || field.id || idx}
                type={field.type || 'text'}
                label={field.label || field.name}
                placeholder={field.placeholder}
                value={formData[field.name || field.id] ?? ''}
                onChange={(e) => handleFormFieldChange(field.name || field.id, e?.target?.value ?? e)}
                required={field.required}
                options={field.options}
                min={field.min}
                max={field.max}
                step={field.step}
              />
            ))}
          </Box>
          <Button
            variant="contained"
            onClick={handleFormSubmit}
            disabled={formSubmitting || activeFlowState.formFields.some((f) => f.required && !(formData[f.name || f.id] ?? '').toString().trim())}
            startIcon={formSubmitting ? <CircularProgress size={16} /> : null}
          >
            {formSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </Paper>
      )}
      {(structuredOutput?.ai_response || structuredOutput?.message) && (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {toDisplayString(structuredOutput.ai_response ?? structuredOutput.message, '')}
              </Typography>
            </Box>
            {typeof window !== 'undefined' && window.speechSynthesis && (
              <Tooltip title="Listen">
                <IconButton
                  size="small"
                  onClick={() => {
                    const txt = toDisplayString(structuredOutput.ai_response ?? structuredOutput.message, '');
                    if (window.speechSynthesis) window.speechSynthesis.cancel();
                    const u = new SpeechSynthesisUtterance(txt);
                    window.speechSynthesis.speak(u);
                  }}
                  sx={isSFF ? { minWidth: 44, minHeight: 44 } : {}}
                >
                  <VolumeUpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: 'auto', flexWrap: 'wrap' }}>
        <TextField
          fullWidth
          size="small"
          placeholder={voice.conversationMode ? 'Listening — speak your question…' : 'Type or tap mic to speak…'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={3}
          sx={{ flex: '1 1 200px' }}
          InputProps={isSFF ? { sx: { minHeight: 44 } } : {}}
        />
        <DemoVoiceControls
          label=""
          hasStt={voice.hasStt}
          hasTts={voice.hasTts}
          isListening={voice.isListening}
          isSpeaking={voice.isSpeaking}
          conversationMode={voice.conversationMode}
          disabled={!hasDefaultFlow || loading}
          onToggleListen={() => (voice.isListening ? voice.stopListening() : voice.startListening())}
          onToggleConversation={() => {
            const next = !voice.conversationMode;
            voice.setConversationMode(next);
            if (next) {
              try { voice.startListening(); } catch { /* ignore */ }
            } else {
              voice.stopAll();
            }
          }}
          onSpeak={() => {
            const txt = toDisplayString(
              structuredOutput?.ai_response
                ?? structuredOutput?.message
                ?? structuredOutput?.routing?.rationale
                ?? '',
              '',
            );
            if (txt) voice.speakReply(txt, { force: true });
          }}
          onStopSpeak={voice.cancelSpeech}
        />
        <IconButton color="primary" onClick={handleSend} disabled={!input.trim() || !hasDefaultFlow || loading} sx={isSFF ? { minWidth: 44, minHeight: 44 } : {}}>
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
